/**
 * Effort Time Utilities
 *
 * Handles conversion between effort hours (float) and display strings.
 * Provides stepper navigation and validation.
 */

// Allowed effort hour values (21 steps)
export const EFFORT_STEPS: number[] = [
  0.25, 0.5, 0.75, 1, 1.5, 2, 2.5, 3, 4, 5,
  7.5, 10, 15, 20, 24, 48, 72, 96, 120, 144, 168,
];

// Display labels for each step
const EFFORT_DISPLAY_MAP: Record<number, string> = {
  0.25: '15 min',
  0.5: '30 min',
  0.75: '45 min',
  1: '1 hr',
  1.5: '1.5 hr',
  2: '2 hr',
  2.5: '2.5 hr',
  3: '3 hr',
  4: '4 hr',
  5: '5 hr',
  7.5: '7.5 hr',
  10: '10 hr',
  15: '15 hr',
  20: '20 hr',
  24: '1 day',
  48: '2 day',
  72: '3 day',
  96: '4 day',
  120: '5 day',
  144: '6 day',
  168: '1 week',
};

// Reverse map for display to hours
const DISPLAY_TO_HOURS_MAP: Record<string, number> = Object.fromEntries(
  Object.entries(EFFORT_DISPLAY_MAP).map(([hours, display]) => [display, parseFloat(hours)])
);

/**
 * Default effort hours value (15 min)
 */
export const DEFAULT_EFFORT_HOURS = 0.25;

/**
 * Convert effort hours to display string
 * @param hours - Float hours value
 * @returns Display string like "1.5 hr", "2 day", etc.
 */
export function effortHoursToDisplay(hours: number | undefined | null): string {
  if (hours === undefined || hours === null) {
    return EFFORT_DISPLAY_MAP[DEFAULT_EFFORT_HOURS];
  }
  return EFFORT_DISPLAY_MAP[hours] || EFFORT_DISPLAY_MAP[DEFAULT_EFFORT_HOURS];
}

/**
 * Convert display string to effort hours
 * @param display - Display string like "1.5 hr", "2 day", etc.
 * @returns Float hours value
 */
export function effortDisplayToHours(display: string): number {
  return DISPLAY_TO_HOURS_MAP[display] ?? DEFAULT_EFFORT_HOURS;
}

/**
 * Check if a value is a valid effort hours step
 * @param hours - Float hours value
 * @returns true if valid step
 */
export function isValidEffortHours(hours: number | undefined | null): boolean {
  if (hours === undefined || hours === null) return false;
  return EFFORT_STEPS.includes(hours);
}

/**
 * Get the next higher step value
 * @param currentHours - Current effort hours
 * @returns Next step up, or max if already at max
 */
export function getEffortStepUp(currentHours: number | undefined | null): number {
  const current = currentHours ?? DEFAULT_EFFORT_HOURS;
  const currentIndex = EFFORT_STEPS.indexOf(current);

  if (currentIndex === -1) {
    // Invalid value - find nearest higher step
    return getNearestHigherEffortStep(current);
  }

  if (currentIndex >= EFFORT_STEPS.length - 1) {
    // Already at max
    return EFFORT_STEPS[EFFORT_STEPS.length - 1];
  }

  return EFFORT_STEPS[currentIndex + 1];
}

/**
 * Get the next lower step value
 * @param currentHours - Current effort hours
 * @returns Next step down, or min if already at min
 */
export function getEffortStepDown(currentHours: number | undefined | null): number {
  const current = currentHours ?? DEFAULT_EFFORT_HOURS;
  const currentIndex = EFFORT_STEPS.indexOf(current);

  if (currentIndex === -1) {
    // Invalid value - find nearest lower step
    for (let i = EFFORT_STEPS.length - 1; i >= 0; i--) {
      if (EFFORT_STEPS[i] < current) {
        return EFFORT_STEPS[i];
      }
    }
    return EFFORT_STEPS[0];
  }

  if (currentIndex <= 0) {
    // Already at min
    return EFFORT_STEPS[0];
  }

  return EFFORT_STEPS[currentIndex - 1];
}

/**
 * Get the nearest higher (or equal) valid effort step for a given value
 * Useful for aggregating sub-item effort to a valid step
 * @param hours - Any float hours value
 * @returns Nearest higher valid step
 */
export function getNearestHigherEffortStep(hours: number): number {
  if (hours <= 0) return EFFORT_STEPS[0];

  for (const step of EFFORT_STEPS) {
    if (step >= hours) {
      return step;
    }
  }

  // Exceeds max, return max
  return EFFORT_STEPS[EFFORT_STEPS.length - 1];
}

/**
 * Normalize effort hours to a valid step
 * Returns nearest valid step, or default if invalid
 * @param hours - Any float value
 * @returns Valid effort step
 */
export function normalizeEffortHours(hours: number | undefined | null): number {
  if (hours === undefined || hours === null || hours <= 0) {
    return DEFAULT_EFFORT_HOURS;
  }

  if (isValidEffortHours(hours)) {
    return hours;
  }

  return getNearestHigherEffortStep(hours);
}
